import React, {Component} from 'react';
import ReactCrop from 'react-image-crop';
import Modal from 'react-modal';

import Spinner from 'shared_features/spinner';
import styles from './styles.module.scss';

class OrganizationLogo extends Component {

    constructor(props) {
        super(props);
        this.uploader = React.createRef();

        this.state = {
            image: null, // Base64 src for the image
            percentCrop: null,
            crop: {
                aspect: 1/1,
                x: 0,
                y: 0,
                width: 30,
                height: 30,
            },
            error: null, // Error message for file validation/loading
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isUploadingLogo && !this.props.isUploadingLogo) {
            this._closeLogoModal();
        }
    }

    _clickedPhoto() {
        this.uploader.current.click();
    }

    _selectedImage(event) {
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

        // Clear any previous error state
        this.setState({ error: null });

        try {
            if (event.target.files && event.target.files[0]) {
                const file = event.target.files[0];

                // Validate file type
                if (!ALLOWED_TYPES.includes(file.type)) {
                    const error = `Invalid file type. Please select a PNG, JPEG, or WebP image.`;
                    console.error('File validation error:', error);
                    this.setState({ error });
                    if (this.props.onError) {
                        this.props.onError(error);
                    }
                    return;
                }

                // Validate file size
                if (file.size > MAX_FILE_SIZE) {
                    const error = `File size too large. Please select an image smaller than 5MB.`;
                    console.error('File validation error:', error);
                    this.setState({ error });
                    if (this.props.onError) {
                        this.props.onError(error);
                    }
                    return;
                }

                let reader = new FileReader();

                reader.onload = (e) => {
                    this.setState({
                        image: e.target.result,
                        error: null
                    });
                };

                reader.onerror = (e) => {
                    const error = `Failed to read file. The image may be corrupted.`;
                    console.error('FileReader error:', e, error);
                    this.setState({ error });
                    if (this.props.onError) {
                        this.props.onError(error);
                    }
                };

                reader.readAsDataURL(file);
            }
        } catch (error) {
            const errorMessage = `Unexpected error while processing file: ${error.message}`;
            console.error('File processing error:', error);
            this.setState({ error: errorMessage });
            if (this.props.onError) {
                this.props.onError(errorMessage);
            }
        } finally {
            // Always clear the input value
            if (this.uploader.current) {
                this.uploader.current.value = "";
            }
        }
    }

    _getCroppedImg(image, percentCrop, fileName="logofile") {
        const x = percentCrop.x / 100 * image.width;
        const y = percentCrop.y / 100 * image.height;
        const width = percentCrop.width / 100 * image.width;
        const height = percentCrop.height / 100 * image.height;
        const finalWidth = 116;
        const finalHeight = 116;
        const canvas = document.createElement('canvas');
        canvas.width = finalWidth;
        canvas.height = finalHeight;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
          image,
          x,
          y,
          width,
          height,
          0,
          0,
          finalWidth,
          finalHeight
        );

        return new Promise((resolve, reject) => {
            canvas.toBlob(file => {
                file.name = fileName;
                resolve(file);
            }, 'image/png');
        });
    }

    _upload() {
        var that = this;
        var image = new Image();
        image.onload = async function () {
            // Guard against null percentCrop by providing a fallback that represents the full image
            const fallbackCrop = { x: 0, y: 0, width: 100, height: 100 };
            const cropToUse = that.state.percentCrop || fallbackCrop;
            const croppedImg = await that._getCroppedImg(image, cropToUse);
            that.props.upload(croppedImg);
            if (!that.props.isUploadingLogo) {
                that._closeLogoModal();
            }
        };
        image.onerror = () => {
            alert('Failed to load image. Please try again.');
            that._closeLogoModal();
        };
        image.src = this.state.image;
    }

    _closeLogoModal() {
        this.setState({
            image: null,
            percentCrop: null,
            crop: {
                aspect: 1/1,
                x: 0,
                y: 0,
                width: 30,
                height: 30,
            },
            error: null
        });
    }

    _renderLogo() {
        const hasLogo = this.props.logoURL && this.props.logoURL !== "https://profiles.reponestrength.com/default.png";

        return (
            <div className={styles.container} onClick={() => this._clickedPhoto()}>
                <div className={styles.logoContainerSmall}>
                    {hasLogo ? (
                        <img alt="organization logo" className={styles.logoImg} src={this.props.logoURL} loading="lazy" />
                    ) : (
                        <div className={styles.logoPlaceholder}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="8" width="18" height="12" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                                <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>
                                <circle cx="8" cy="16" r="1" fill="currentColor"/>
                                <circle cx="16" cy="16" r="1" fill="currentColor"/>
                            </svg>
                        </div>
                    )}
                    <div className={styles.logoOverlay}>
                        <img src="https://assets.reponestrength.com/edit.png" alt="Edit logo" loading="lazy" />
                    </div>
                </div>
            </div>
        );
    }

    _renderCrop() {
        if (!this.state.image) {
            return null;
        }

        return <ReactCrop
                    className={styles.crop}
                    src={this.state.image}
                    crop={this.state.crop}
                    aspect={1}
                    onChange={(crop, percentCrop) => {
                        this.setState({ crop, percentCrop });
                    }} />
    }

    _renderError() {
        if (this.state.error) {
            return <div className={styles.error}>{this.state.error}</div>;
        }
        return null;
    }

    _renderDone() {
        if (this.props.isUploadingLogo) {
            return <div className={styles.upload}>
                <Spinner />
                <div className={styles.uploadText}>Uploading...</div>
            </div>
        } else {
            return <div className={styles.upload} onClick={() => this._upload()}>Done</div>;
        }
    }

    render() {
        return (
            <div>
                {this._renderLogo()}

                <div>
                    <input type="file"
                        ref={this.uploader}
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={(event) => this._selectedImage(event)} />
                </div>

                <Modal
                    className={styles.modal}
                    overlayClassName={styles.overlay}
                    isOpen={this.state.image !== null}
                    onRequestClose={() => this._closeLogoModal()}>
                    <h3 className={styles.modalTitle}>Crop Your Logo</h3>
                    {this._renderError()}
                    {this._renderCrop()}

                    {this._renderDone()}
                </Modal>

            </div>
        );
    }

}

export default OrganizationLogo;